import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { isDarkMode } from "../../../utils/constants";
import { isMobileBrowser } from "../../../utils/user_agent";
import {
  alignFundamentalSeries,
  createMainSeriesData,
  formatCrosshairTime,
  formatFundamentalDate,
  formatFundamentalValue,
  formatTickMark,
  getInitialLogicalRange,
  logicalRangesClose,
  resolveChartScreenshotCanvas,
} from "./lightweightChartUtils";
import {
  installAttributionHider,
  loadLightweightCharts,
} from "./lightweightChartsLoader";

const getPalette = () => {
  if (isDarkMode) {
    return {
      background: "#0d1117",
      text: "#cbd5e1",
      mutedText: "#94a3b8",
      accent: "#7aa2ff",
      border: "rgba(148, 163, 184, 0.24)",
      grid: "rgba(148, 163, 184, 0.12)",
      crosshair: "rgba(226, 232, 240, 0.26)",
    };
  }

  return {
    background: "#f8fafc",
    text: "#334155",
    mutedText: "#64748b",
    accent: "#2563eb",
    border: "rgba(100, 116, 139, 0.18)",
    grid: "rgba(100, 116, 139, 0.12)",
    crosshair: "rgba(30, 41, 59, 0.18)",
  };
};

const buildChartOptions = ({ library, selectedScale, isMobile }) => {
  const palette = getPalette();

  return {
    layout: {
      background: { color: palette.background },
      textColor: palette.text,
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    attributionLogo: { visible: false },
    grid: {
      vertLines: { color: palette.grid },
      horzLines: { color: palette.grid },
    },
    crosshair: {
      mode: library?.CrosshairMode?.Normal ?? 0,
      vertLine: {
        color: palette.crosshair,
        width: 1,
        style: 2,
      },
      horzLine: {
        color: palette.crosshair,
        width: 1,
        style: 2,
      },
    },
    rightPriceScale: {
      borderColor: palette.border,
      scaleMargins: { top: 0.12, bottom: 0.08 },
    },
    leftPriceScale: { visible: false },
    timeScale: {
      borderColor: palette.border,
      timeVisible: !["1d", "1w", "1mo"].includes(String(selectedScale || "")),
      secondsVisible: false,
      rightOffset: 5,
      rightBarStaysOnScroll: true,
      barSpacing: isMobile ? 8 : 10,
      minBarSpacing: 3,
      tickMarkFormatter: (time) => formatTickMark(time, selectedScale),
    },
    handleScroll: isMobile
      ? {
          mouseWheel: false,
          pressedMouseMove: false,
          horzTouchDrag: true,
          vertTouchDrag: false,
        }
      : {
          mouseWheel: true,
          pressedMouseMove: true,
          horzTouchDrag: true,
          vertTouchDrag: false,
        },
    handleScale: isMobile
      ? {
          mouseWheel: false,
          axisPressedMouseMove: false,
          pinch: true,
        }
      : {
          mouseWheel: true,
          axisPressedMouseMove: true,
          pinch: true,
        },
    localization: {
      timeFormatter: (time) => formatCrosshairTime(time, selectedScale),
    },
  };
};

const FundamentalChart = ({
  label,
  values,
  tickerPrices,
  selectedScale,
  height = 200,
  outerMarginTop = 1.5,
  isChartVisible = true,
  sharedRangeBus = null,
  onChartReady = null,
  publishSharedRange = false,
  userTimezone = null,
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const attributionObserverRef = useRef(null);
  const suppressRangeSyncRef = useRef(false);
  const userHasPannedRef = useRef(false);
  const prevSeriesKeyRef = useRef(null);
  const syncIdRef = useRef(`fund-sync-${label || "fundamental"}`);
  const [library, setLibrary] = useState(() => window.LightweightCharts || null);
  const [loadError, setLoadError] = useState(null);
  const [containerMountKey, setContainerMountKey] = useState(0);
  const isMobile = isMobileBrowser();

  const setChartContainerRef = useCallback((node) => {
    containerRef.current = node;
    if (node) {
      setContainerMountKey((key) => key + 1);
    }
  }, []);

  const fundamentalLabel = label || "Fundamentals";
  const isVolumeChart =
    typeof fundamentalLabel === "string" &&
    fundamentalLabel.toLowerCase().includes("volume");
  const isInsiderChart =
    typeof fundamentalLabel === "string" &&
    fundamentalLabel.toLowerCase().includes("insider");
  const priceSeries = useMemo(() => {
    return createMainSeriesData(tickerPrices || [], userTimezone);
  }, [tickerPrices, userTimezone]);
  const priceTimeSeries = priceSeries.times;
  const volumeSeries = useMemo(
    () =>
      priceSeries.candleData
        .filter((point) => Number(point?.volume) > 0)
        .map((point) => ({
          time: point.time,
          value: Number(point.volume),
        })),
    [priceSeries],
  );

  const mappedSeries = useMemo(
    () => {
      if (isVolumeChart) return volumeSeries;

      return alignFundamentalSeries({
        values: values || [],
        priceTimes: priceTimeSeries,
        timeZone: userTimezone,
      });
    },
    [isVolumeChart, volumeSeries, values, priceTimeSeries, userTimezone]
  );

  const latestSummary = useMemo(() => {
    const latest = mappedSeries[mappedSeries.length - 1];
    if (!latest) {
      return { value: "-", date: "" };
    }
    return {
      value: formatFundamentalValue(latest.value),
      date: formatFundamentalDate(latest.time * 1000, selectedScale),
    };
  }, [mappedSeries, selectedScale]);

  const useHistogram = !isInsiderChart && (isVolumeChart || mappedSeries.length > 15);

  const mappedSeriesKey = useMemo(
    () =>
      mappedSeries
        .map((point) => `${point.time}:${Number(point.value)}`)
        .join("|"),
    [mappedSeries]
  );

  useEffect(() => {
    let cancelled = false;

    loadLightweightCharts()
      .then((api) => {
        if (cancelled) return;
        setLibrary(api);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!library || !isChartVisible || !containerRef.current || containerMountKey === 0) return undefined;

    const container = containerRef.current;
    const chart = library.createChart(container, {
      width: Math.max(container.clientWidth || 0, 320),
      height: Math.max(container.clientHeight || 0, 180),
      ...buildChartOptions({ library, selectedScale, isMobile }),
    });

    chart.applyOptions({ attributionLogo: { visible: false } });
    chartRef.current = chart;
    attributionObserverRef.current = installAttributionHider(container);
    const timeScale = chart.timeScale();

    const handleVisibleRangeChange = (range) => {
      // Any range change not triggered by our own setVisibleLogicalRange calls
      // means the user scrolled/zoomed — preserve that position on re-renders.
      if (!suppressRangeSyncRef.current) {
        userHasPannedRef.current = true;
      }
      if (!publishSharedRange) return;
      if (!sharedRangeBus?.setRange) return;
      if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) {
        return;
      }
      if (suppressRangeSyncRef.current) return;
      sharedRangeBus.setRange(range, syncIdRef.current);
    };

    if (timeScale?.subscribeVisibleLogicalRangeChange) {
      timeScale.subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
    }

    const unsubscribeSharedRange = sharedRangeBus?.subscribe?.(
      (range, sourceId) => {
        if (sourceId === syncIdRef.current) return;
        if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) {
          return;
        }
        const current = chart.timeScale()?.getVisibleLogicalRange?.();
        if (logicalRangesClose(current, range)) return;
        suppressRangeSyncRef.current = true;
        chart.timeScale().setVisibleLogicalRange(range);
        window.requestAnimationFrame(() => {
          suppressRangeSyncRef.current = false;
        });
      }
    );

    if (typeof ResizeObserver !== "undefined") {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const width = Math.max(entry.contentRect.width || 0, 320);
        const heightPx = Math.max(entry.contentRect.height || 0, 180);
        chart.resize(width, heightPx);
      });
      resizeObserverRef.current.observe(container);
    }

    if (typeof onChartReady === "function") {
      onChartReady({
        getScreenshotCanvas: () =>
          resolveChartScreenshotCanvas(chartRef.current, containerRef.current),
        getVisibleLogicalRange: () =>
          chartRef.current?.timeScale()?.getVisibleLogicalRange?.() || null,
      });
    }

    return () => {
      if (timeScale?.unsubscribeVisibleLogicalRangeChange) {
        timeScale.unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
      }
      if (typeof unsubscribeSharedRange === "function") {
        unsubscribeSharedRange();
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (attributionObserverRef.current) {
        attributionObserverRef.current.disconnect();
        attributionObserverRef.current = null;
      }
      if (typeof onChartReady === "function") {
        onChartReady(null);
      }
      seriesRef.current = null;
      chartRef.current?.remove();
      chartRef.current = null;
      // Chart is being destroyed — reset pan state so the replacement chart
      // starts at the correct initial range.
      userHasPannedRef.current = false;
      prevSeriesKeyRef.current = null;
    };
  }, [
    library,
    isChartVisible,
    containerMountKey,
    onChartReady,
    selectedScale,
    isMobile,
    sharedRangeBus,
    publishSharedRange,
  ]);

  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions(
      buildChartOptions({ library, selectedScale, isMobile })
    );
    chartRef.current.applyOptions({ attributionLogo: { visible: false } });
  }, [library, selectedScale, isMobile]);

  useEffect(() => {
    if (!chartRef.current || !library) return;

    if (mappedSeries.length === 0) {
      if (seriesRef.current) {
        chartRef.current.removeSeries(seriesRef.current);
        seriesRef.current = null;
      }
      prevSeriesKeyRef.current = null;
      userHasPannedRef.current = false;
      return;
    }

    const chart = chartRef.current;
    const palette = getPalette();

    // Include values in the identity key so ticker/series changes with the same
    // date span still redraw the chart.
    const first = mappedSeries[0];
    const last = mappedSeries[mappedSeries.length - 1];
    const seriesKey = `${fundamentalLabel}-${first?.time ?? ""}-${last?.time ?? ""}-${mappedSeries.length}-${useHistogram ? "h" : "l"}-${mappedSeriesKey}`;
    const isNewDataset = seriesKey !== prevSeriesKeyRef.current;

    if (!isNewDataset && seriesRef.current) {
      // Same data as last render (parent re-rendered due to unrelated state
      // like live price ticks). Skip series recreation and range reset entirely
      // to avoid both the performance cost and any auto-fit snap from
      // lightweight-charts receiving a brand-new series.
      return;
    }

    if (isNewDataset) {
      userHasPannedRef.current = false;
      prevSeriesKeyRef.current = seriesKey;
    }

    if (seriesRef.current) {
      chart.removeSeries(seriesRef.current);
      seriesRef.current = null;
    }

    let series;
    if (useHistogram) {
      series = chart.addHistogramSeries({
        color: palette.accent,
        base: 0,
        priceFormat: {
          type: "custom",
          formatter: formatFundamentalValue,
        },
      });
    } else {
      series = chart.addLineSeries({
        color: palette.accent,
        lineWidth: 2,
        crosshairMarkerVisible: !isInsiderChart,
        lastValueVisible: true,
        priceLineVisible: false,
        priceFormat: {
          type: "custom",
          formatter: formatFundamentalValue,
        },
      });
    }

    series.setData(mappedSeries);
    seriesRef.current = series;

    const busRange = sharedRangeBus?.getRange?.() ?? null;
    const initialRange = getInitialLogicalRange(mappedSeries.length, selectedScale);
    const nextRange = busRange || initialRange;
    const currentRange = chart.timeScale()?.getVisibleLogicalRange?.();

    if (
      nextRange &&
      chart.timeScale()?.setVisibleLogicalRange &&
      !logicalRangesClose(currentRange, nextRange)
    ) {
      suppressRangeSyncRef.current = true;
      chart.timeScale().setVisibleLogicalRange(nextRange);
      window.requestAnimationFrame(() => {
        suppressRangeSyncRef.current = false;
      });
    }
  }, [library, mappedSeries, mappedSeriesKey, selectedScale, useHistogram, isInsiderChart, sharedRangeBus, fundamentalLabel, containerMountKey]);

  if (
    !isChartVisible ||
    (!isVolumeChart && (!Array.isArray(values) || values.length === 0)) ||
    (isVolumeChart && mappedSeries.length === 0)
  ) {
    return null;
  }

  const palette = getPalette();

  return (
    <Box sx={{ mt: outerMarginTop }}>
      <Box
        sx={{
          width: "100%",
          height,
          borderRadius: "14px",
          padding: "10px 12px 8px",
          boxSizing: "border-box",
          background: isDarkMode
            ? "linear-gradient(180deg, #0f1115 0%, #0b0d10 100%)"
            : "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
          border: isDarkMode ? "1px solid #1f2937" : "1px solid #e5e7eb",
          boxShadow: isDarkMode
            ? "0 6px 16px rgba(0,0,0,0.35)"
            : "0 8px 18px rgba(15,23,42,0.08)",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: "8px",
            px: "4px",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: palette.text,
              textTransform: "none",
              letterSpacing: "0.01em",
            }}
          >
            {fundamentalLabel}
          </Typography>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: palette.accent,
                lineHeight: 1.1,
              }}
            >
              {latestSummary.value}
            </Typography>
            {latestSummary.date ? (
              <Typography
                sx={{
                  fontSize: "11px",
                  color: palette.mutedText,
                  lineHeight: 1.1,
                }}
              >
                {latestSummary.date}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {loadError ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: palette.mutedText,
                fontSize: "13px",
              }}
            >
              Chart unavailable
            </Box>
          ) : (
            <div
              ref={setChartContainerRef}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
                position: "relative",
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FundamentalChart;
